import { useEffect, useState } from "react";
import { Search, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { estApi } from "../lib/api";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;

/** Search the cost library and hand back one item. Used to fix a low-confidence
 *  FRS/takeoff match by hand — the pick is remembered as an alias on commit. */
export default function ItemPickerDialog({ open, onOpenChange, initialSearch = "", onPick }) {
  const [search, setSearch] = useState(initialSearch);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) setSearch(initialSearch); }, [open, initialSearch]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const t = setTimeout(() => {
      estApi.getPriceList({ search, active: true, limit: 40, sort: "name" })
        .then((d) => setItems(d.items || []))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(t);
  }, [open, search]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="item-picker-dialog">
        <DialogHeader>
          <DialogTitle>Pick the right cost library item</DialogTitle>
          <DialogDescription className="text-xs">
            Your choice is saved as an alias, so this part matches automatically next time.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input autoFocus value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search..."
                 className="h-9 pl-8 text-xs" data-testid="item-picker-search" />
        </div>

        <div className="max-h-80 overflow-auto rounded border border-slate-200">
          {loading && (
            <div className="flex items-center justify-center py-10 text-xs text-slate-400">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Searching…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="py-10 text-center text-xs text-slate-400">No matching items</div>
          )}
          {!loading && items.map((it) => (
            <button key={it.id}
                    onClick={() => { onPick(it); onOpenChange(false); }}
                    className="flex w-full items-center gap-3 border-b border-slate-100 px-3 py-2 text-left last:border-0 hover:bg-slate-50"
                    data-testid={`item-picker-option-${it.id}`}>
              <span className="w-32 shrink-0 truncate font-mono text-[11px] text-slate-500">
                {it.item_number || "—"}
              </span>
              <span className="min-w-0 flex-1 truncate text-xs text-slate-800">{it.name}</span>
              <span className="w-24 shrink-0 truncate text-[11px] text-slate-400">
                {it.manufacturer}
              </span>
              <span className="w-20 shrink-0 text-right text-xs tabular-nums text-slate-600">
                {it.has_cost ? money(it.unit_cost) : "no cost"}
              </span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
